import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import * as docusignAPI from "../api/docusignAPI";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";

const DS_SCRIPT_SRC = "https://www.docusign.com/scripts/ds-digital-signup.js";
const DOCUSIGN_PARTNER_IK = process.env.REACT_APP_DOCUSIGN_CLIENT_ID;

function Integrations({ user }) {
  const { toast } = useToast();
  const [docusignConnected, setDocusignConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [dsScriptReady, setDsScriptReady] = useState(false);

  const fetchIntegrations = async () => {
    try {
      const data = await docusignAPI.getIntegrations();
      setDocusignConnected(data.docusign === true);
    } catch (error) {
      console.error("Error fetching integrations:", error);
      toast.error(humanizeError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  useEffect(() => {
    const onScriptReady = () => {
      setDsScriptReady(true);
      if (window.DSDigitalSignup?.registerSignupActivationCallback) {
        window.DSDigitalSignup.registerSignupActivationCallback(() => {
          fetchIntegrations();
          toast.success("Docusign trial activated and connected.");
          setTrialLoading(false);
        });
      }
      if (window.DSDigitalSignup?.registerSignupPopupCloseCallback) {
        window.DSDigitalSignup.registerSignupPopupCloseCallback(() => {
          setTrialLoading(false);
        });
      }
    };

    if (window.DSDigitalSignup) {
      onScriptReady();
    } else {
      let script = document.querySelector(`script[src="${DS_SCRIPT_SRC}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = DS_SCRIPT_SRC;
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener("load", onScriptReady);
    }

    const onTrialErrorMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== "docusign-trial-error") return;
      toast.error(`Docusign trial signup failed: ${e.data.error || "unknown error"}.`);
      setTrialLoading(false);
    };
    window.addEventListener("message", onTrialErrorMessage);

    return () => {
      const script = document.querySelector(`script[src="${DS_SCRIPT_SRC}"]`);
      if (script) script.removeEventListener("load", onScriptReady);
      window.removeEventListener("message", onTrialErrorMessage);
    };
  }, []);

  const handleEnable = async () => {
    setActionLoading(true);
    try {
      await docusignAPI.enable();
      setActionLoading(false);
    } catch (error) {
      setActionLoading(false);
      toast.error(humanizeError(error));
    }
  };

  const handleTryFree = () => {
    if (!window.DSDigitalSignup?.startSignup) {
      toast.error("Docusign signup is still loading. Please try again in a moment.");
      return;
    }
    if (!DOCUSIGN_PARTNER_IK) {
      toast.error("Docusign is not configured. Contact support.");
      return;
    }
    setTrialLoading(true);
    window.DSDigitalSignup.startSignup({
      partnerIK: DOCUSIGN_PARTNER_IK,
      loginRedirectUri: `${window.location.origin}/integrations/docusign-trial-callback`,
      firstName: user?.first_name || "",
      lastName: user?.last_name || "",
      email: user?.email || "",
    });
  };

  const handleDisable = async () => {
    setActionLoading(true);
    try {
      await docusignAPI.disable();
      await fetchIntegrations();
      toast.success("Docusign integration disabled.");
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const spinner = (
    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
      <Header user={user} />
      <div className="flex flex-1">
        <Sidebar />
        <main className="flex-1 p-8 bg-gradient-to-b from-transparent to-emerald-50/10">
          <div className="max-w-7xl mx-auto w-full">
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-100/50 shadow-xl">
                <h1 className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-gray-900 bg-clip-text text-transparent mb-3">
                  Integrations
                </h1>
                <p className="text-gray-600 text-lg font-medium">
                  Connect and manage third-party integrations
                </p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
                <p className="mt-4 text-gray-600 font-medium">
                  Loading integrations...
                </p>
              </div>
            ) : (
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl border border-emerald-100/50 shadow-xl p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <img
                      src="/docusign.png"
                      alt="Docusign"
                      className="w-16 h-16 object-contain flex-shrink-0"
                    />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        Docusign
                      </h2>
                      <p className="text-gray-600 text-sm mt-0.5">
                        {docusignConnected
                          ? "Connected. You can send documents for signature from household details."
                          : "Connect Docusign to send household forms for signature."}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {docusignConnected ? (
                      <button
                        onClick={handleDisable}
                        disabled={actionLoading}
                        className="bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold px-6 py-3 shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                      >
                        {actionLoading ? (
                          <>
                            {spinner}
                            Disabling...
                          </>
                        ) : (
                          "Disable"
                        )}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleTryFree}
                          disabled={trialLoading || !dsScriptReady}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold px-6 py-3 shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                        >
                          {trialLoading ? (
                            <>
                              {spinner}
                              Opening...
                            </>
                          ) : (
                            "Try Docusign for Free"
                          )}
                        </button>
                        <button
                          onClick={handleEnable}
                          disabled={actionLoading}
                          className="bg-white hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-700 border-2 border-emerald-600 rounded-xl font-semibold px-6 py-3 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                        >
                          {actionLoading ? (
                            <>
                              {spinner}
                              Redirecting...
                            </>
                          ) : (
                            "Connect Existing Docusign Account"
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Integrations;
